/* eslint-disable */
import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { uploadMultipleFile } from "utils/Gateway";
import * as url from "helper/UrlHelper";

// ── helpers ───────────────────────────────────────────────────────────────────

const formatBytes = (bytes = 0) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getExtension = (name = "") => (name.split(".").pop() || "").toLowerCase();

const FILE_ICONS = {
  image: { icon: "image", color: "#10B981", bg: "#D1FAE5" },
  video: { icon: "play_circle", color: "#8B5CF6", bg: "#EDE9FE" },
  audio: { icon: "graphic_eq", color: "#F59E0B", bg: "#FEF3C7" },
  pdf: { icon: "picture_as_pdf", color: "#EF4444", bg: "#FEE2E2" },
  word: { icon: "description", color: "#2563EB", bg: "#DBEAFE" },
  excel: { icon: "table_chart", color: "#059669", bg: "#D1FAE5" },
  ppt: { icon: "co_present", color: "#EA580C", bg: "#FFEDD5" },
  text: { icon: "article", color: "#64748B", bg: "#F1F5F9" },
  code: { icon: "code", color: "#7C3AED", bg: "#EDE9FE" },
  other: { icon: "insert_drive_file", color: "#6B7280", bg: "#F3F4F6" },
};

const TYPE_MAP = {
  image: ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"],
  video: ["mp4", "avi", "mov", "webm", "mkv", "flv"],
  audio: ["m4a", "mp3", "wav", "ogg", "aac", "flac"],
  pdf: ["pdf"],
  word: ["doc", "docx"],
  excel: ["xls", "xlsx", "csv"],
  ppt: ["ppt", "pptx"],
  text: ["txt", "rtf"],
  code: ["py", "js", "ts", "xml", "json", "html", "css", "dll"],
};

const getFileType = (name) => {
  const ext = getExtension(name);
  for (const [type, exts] of Object.entries(TYPE_MAP)) {
    if (exts.includes(ext)) return type;
  }
  return "other";
};

// ── PreviewModal ───────────────────────────────────────────────────────────────

const PreviewModal = ({ entry, onClose }) => {
  const { t } = useTranslation();

  if (!entry) return null;

  const fileType = getFileType(entry.file.name);
  const objectUrl = URL.createObjectURL(entry.file);
  const isPreviewable = ["image", "video", "audio"].includes(fileType);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = entry.file.name;
    a.click();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          maxWidth: 780,
          width: "100%",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 18px",
            borderBottom: "1px solid #E2E8F0",
            background: "#F8FAFC",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: 20,
                color: FILE_ICONS[fileType]?.color || "#6B7280",
              }}
            >
              {FILE_ICONS[fileType]?.icon || "insert_drive_file"}
            </span>
            <span
              style={{
                fontWeight: 600,
                fontSize: "0.9rem",
                color: "#1E293B",
                maxWidth: 460,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={entry.file.name}
            >
              {entry.file.name}
            </span>
            <span style={{ color: "#94A3B8", fontSize: "0.78rem" }}>
              ({formatBytes(entry.file.size)})
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleDownload}
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              title={t("Download")}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 16 }}
              >
                download
              </span>
              {t("Download")}
            </button>
            <button
              onClick={onClose}
              className="btn btn-sm btn-outline-secondary"
              style={{ lineHeight: 1 }}
              title={t("Close")}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 18 }}
              >
                close
              </span>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1.5rem",
            background: fileType === "image" ? "#0F172A" : "#fff",
            minHeight: 200,
          }}
        >
          {fileType === "image" && (
            <img
              src={objectUrl}
              alt={entry.file.name}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 6,
              }}
            />
          )}

          {fileType === "video" && (
            <video
              src={objectUrl}
              controls
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                borderRadius: 6,
                background: "#000",
              }}
            />
          )}

          {fileType === "audio" && (
            <div style={{ textAlign: "center", width: "100%" }}>
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: 64,
                  color: "#F59E0B",
                  marginBottom: 16,
                  display: "block",
                }}
              >
                graphic_eq
              </span>
              <p
                style={{ color: "#475569", marginBottom: 16, fontWeight: 500 }}
              >
                {entry.file.name}
              </p>
              <audio
                src={objectUrl}
                controls
                style={{ width: "100%", maxWidth: 420 }}
              />
            </div>
          )}

          {!isPreviewable && (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background: FILE_ICONS[fileType]?.bg || "#F3F4F6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: 40,
                    color: FILE_ICONS[fileType]?.color || "#6B7280",
                  }}
                >
                  {FILE_ICONS[fileType]?.icon || "insert_drive_file"}
                </span>
              </div>
              <p style={{ color: "#475569", marginBottom: 6, fontWeight: 500 }}>
                {t("Preview is not available for this file type.")}
              </p>
              <p
                style={{
                  color: "#94A3B8",
                  fontSize: "0.82rem",
                  marginBottom: 20,
                }}
              >
                {t("Download the file to open it on your device.")}
              </p>
              <button
                onClick={handleDownload}
                className="btn btn-primary d-inline-flex align-items-center gap-2"
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 18 }}
                >
                  download
                </span>
                {t("Download File")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── component ─────────────────────────────────────────────────────────────────

const UploadFilePopup = ({ afterUpload = () => {} }) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  const fileInputRef = useRef(null);

  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [previewEntry, setPreviewEntry] = useState(null);

  // ── file selection helpers ─────────────────────────────────────────────────

  const addFiles = (fileList) => {
    const newEntries = Array.from(fileList).map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      file,
    }));
    setPendingFiles((prev) => [...prev, ...newEntries]);
    setUploadError("");
  };

  const removeFile = (id) => {
    setPendingFiles((prev) => prev.filter((entry) => entry.id !== id));
  };

  const resetState = () => {
    setPendingFiles([]);
    setIsDragging(false);
    setIsUploading(false);
    setUploadError("");
    setPreviewEntry(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── download handler ───────────────────────────────────────────────────────

  const handleDownload = (entry) => {
    const objectUrl = URL.createObjectURL(entry.file);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = entry.file.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  };

  // ── drag-and-drop handlers ─────────────────────────────────────────────────

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  // ── upload handler ─────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;

    try {
      setIsUploading(true);
      setUploadError("");

      const requestUrl =
        url.API_BASE_URL + url.API_MULTIPLE_FILE_UPLOAD;

      const fileMap = {};
      pendingFiles.forEach((entry, i) => {
        fileMap[i] = entry.file;
      });

      const response = await uploadMultipleFile(requestUrl, fileMap);

      if (response.status) {
        resetState();
        afterUpload();

        const offcanvasEl = document.getElementById("uploadFilePopup");
        if (offcanvasEl) {
          const bsOffcanvas =
            window.bootstrap?.Offcanvas?.getInstance(offcanvasEl) ||
            new window.bootstrap.Offcanvas(offcanvasEl);
          bsOffcanvas.hide();
        }
      } else {
        setUploadError(
          response.message || t("Upload failed. Please try again."),
        );
        setIsUploading(false);
      }
    } catch (err) {
      setUploadError(t("An error occurred during upload."));
      setIsUploading(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Preview Modal */}
      {previewEntry && (
        <PreviewModal
          entry={previewEntry}
          onClose={() => setPreviewEntry(null)}
        />
      )}

      <div
        className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
        tabIndex="-1"
        id="uploadFilePopup"
        aria-labelledby="uploadFilePopup"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        {/* ── Header ── */}
        <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
          <div className="d-flex align-items-center gap-3">
            <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
              <span className="material-symbols-outlined text-primary fs-5 icon-md">
                upload_file
              </span>
            </div>
            <div>
              <h3
                className="offcanvas-title fw-bold text-white mb-0"
                id="offcanvasLabelDetailsLabel"
              >
                {t("Upload Files")}
              </h3>
              <p className="text-white fs-sm mb-0">
                {t("Add files to your media library")}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={resetState}
          ></button>
        </div>

        {/* ── Body ── */}
        <div className="offcanvas-body p-4">
          {/* Drag-and-drop zone */}
          <div
            className="rounded-3 border-2 border-dashed d-flex flex-column align-items-center justify-content-center gap-2 p-5 mb-4 text-center"
            style={{
              border: `2px dashed ${isDragging ? "var(--bs-primary)" : "#CBD5E1"}`,
              background: isDragging
                ? "rgba(var(--bs-primary-rgb),0.05)"
                : "#F8FAFC",
              cursor: "pointer",
              transition: "border-color 0.2s, background 0.2s",
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mb-1"
              style={{
                width: 56,
                height: 56,
                background: isDragging
                  ? "rgba(var(--bs-primary-rgb),0.12)"
                  : "#E2E8F0",
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: 28,
                  color: isDragging ? "var(--bs-primary)" : "#64748B",
                }}
              >
                cloud_upload
              </span>
            </div>
            <p className="fw-semibold mb-0" style={{ color: "#1E293B" }}>
              {t("Drag & drop files here")}
            </p>
            <p className="text-muted small mb-0">
              {t("or")}{" "}
              <span
                className="text-primary fw-semibold"
                style={{ textDecoration: "underline" }}
              >
                {t("browse to choose files")}
              </span>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="d-none"
              onChange={(e) => {
                if (e.target.files?.length) addFiles(e.target.files);
              }}
            />
          </div>

          {/* Error alert */}
          {uploadError && (
            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 px-3 mb-3">
              <span className="material-symbols-outlined fs-5">error</span>
              <span className="small">{uploadError}</span>
            </div>
          )}

          {/* Pending file list */}
          {pendingFiles.length > 0 && (
            <div>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="fw-semibold mb-0" style={{ color: "#1E293B" }}>
                  {t("Selected Files")}{" "}
                  <span className="badge bg-primary ms-1">
                    {pendingFiles.length}
                  </span>
                </h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger py-0 px-2"
                  onClick={() => setPendingFiles([])}
                  disabled={isUploading}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 16, verticalAlign: "middle" }}
                  >
                    delete_sweep
                  </span>{" "}
                  <span className="small">{t("Clear all")}</span>
                </button>
              </div>

              <div
                className="rounded-3 border overflow-auto"
                style={{ maxHeight: 340 }}
              >
                {pendingFiles.map((entry, idx) => {
                  const fileType = getFileType(entry.file.name);
                  const meta = FILE_ICONS[fileType] || FILE_ICONS.other;
                  const isPreviewable = ["image", "video", "audio"].includes(
                    fileType,
                  );

                  return (
                    <div
                      key={entry.id}
                      className={`d-flex align-items-center gap-3 px-3 py-2${
                        idx < pendingFiles.length - 1 ? " border-bottom" : ""
                      }`}
                      style={{ background: "#fff" }}
                    >
                      {/* File Type Icon */}
                      <div
                        className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                        style={{ width: 38, height: 38, background: meta.bg }}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{ color: meta.color, fontSize: 20 }}
                        >
                          {meta.icon}
                        </span>
                      </div>

                      {/* Name + size */}
                      <div className="flex-grow-1 overflow-hidden">
                        <p
                          className="mb-0 fw-medium text-truncate small"
                          style={{ color: "#1E293B", maxWidth: "100%" }}
                          title={entry.file.name}
                        >
                          {entry.file.name}
                        </p>
                        <p
                          className="mb-0 text-muted"
                          style={{ fontSize: "0.72rem" }}
                        >
                          {formatBytes(entry.file.size)}
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        {/* Preview button */}
                        <button
                          type="button"
                          className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: 28,
                            height: 28,
                            background: isPreviewable ? "#EDE9FE" : "#DBEAFE",
                            borderRadius: 6,
                          }}
                          onClick={() => setPreviewEntry(entry)}
                          disabled={isUploading}
                          title={isPreviewable ? t("Preview") : t("View")}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{
                              fontSize: 16,
                              color: isPreviewable ? "#7C3AED" : "#2563EB",
                            }}
                          >
                            {isPreviewable ? "visibility" : "open_in_new"}
                          </span>
                        </button>

                        {/* Download button */}
                        <button
                          type="button"
                          className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: 28,
                            height: 28,
                            background: "#D1FAE5",
                            borderRadius: 6,
                          }}
                          onClick={() => handleDownload(entry)}
                          disabled={isUploading}
                          title={t("Download")}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{ fontSize: 16, color: "#059669" }}
                          >
                            download
                          </span>
                        </button>

                        {/* Remove button */}
                        <button
                          type="button"
                          className="btn btn-sm p-0 border-0 d-flex align-items-center justify-content-center"
                          style={{
                            width: 28,
                            height: 28,
                            background: "#FEE2E2",
                            borderRadius: 6,
                          }}
                          onClick={() => removeFile(entry.id)}
                          disabled={isUploading}
                          title={t("Remove")}
                        >
                          <span
                            className="material-symbols-outlined"
                            style={{ fontSize: 16, color: "#EF4444" }}
                          >
                            close
                          </span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div
          className="offcanvas-footer p-3 bg-white border-top d-flex align-items-center justify-content-between"
          style={{ position: "sticky", bottom: 0, zIndex: 10 }}
        >
          <span className="text-muted small">
            {pendingFiles.length > 0
              ? `${pendingFiles.length} ${t("file(s) ready to upload")}`
              : t("No files selected")}
          </span>

          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            disabled={isUploading || pendingFiles.length === 0}
            style={{
              cursor:
                isUploading || pendingFiles.length === 0
                  ? "not-allowed"
                  : "pointer",
            }}
            onClick={handleUpload}
          >
            {isUploading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("Uploading...")}
              </>
            ) : (
              <>
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 18 }}
                >
                  cloud_upload
                </span>
                {t("Upload Files")}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default UploadFilePopup;
